import { useEffect, useRef, useState, type MouseEvent } from "react";

type Props = {
  /** Audio file URL. */
  src: string;
  /** Square cover image shown beside the title. */
  cover: string;
  title: string;
  artist: string;
  className?: string;
};

const fmt = (s: number) => {
  if (!Number.isFinite(s) || s <= 0) return "0:00";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
};

/**
 * A discreet sound bar: cover, title, elapsed time, a thin seek line and one
 * play/pause button. Nothing loads until the listener presses play.
 */
export function SoundPlayer({ src, cover, title, artist, className = "" }: Props) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTime = () => setCur(el.currentTime);
    const onMeta = () => setDur(Number.isFinite(el.duration) ? el.duration : 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onPause);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onPause);
    };
  }, []);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setPlaying(false));
    else el.pause();
  };

  const seek = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const total = Number.isFinite(el.duration) ? el.duration : dur;
    if (!total) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * total;
    setCur(el.currentTime);
  };

  return (
    <div className={`flex items-center gap-3 border border-bone/15 bg-ink/85 px-3 py-2 ${className}`}>
      <img src={cover} alt="" loading="lazy" className="h-11 w-11 shrink-0 object-cover" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate font-mono text-[9px] uppercase tracking-[0.24em] text-bone/85">{title}</span>
          <span className="shrink-0 font-mono text-[9px] tracking-[0.16em] text-bone/50">
            {fmt(cur)} / {fmt(dur)}
          </span>
        </div>
        <div className="-my-1 cursor-pointer py-1" onClick={seek}>
          <div className="h-[2px] w-full bg-bone/15">
            <div className="h-full bg-bone/70" style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} />
          </div>
        </div>
        <span className="mt-1 block truncate font-mono text-[8px] uppercase tracking-[0.2em] text-bone/45">
          {artist}
        </span>
      </div>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className="group flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-bone/30 transition-colors hover:border-bone"
      >
        {playing ? (
          <span className="flex gap-[4px]">
            <span className="block h-3 w-[2px] bg-bone/80" />
            <span className="block h-3 w-[2px] bg-bone/80" />
          </span>
        ) : (
          <span className="ml-[2px] block h-0 w-0 border-y-[6px] border-l-[9px] border-y-transparent border-l-bone/80 transition-colors group-hover:border-l-bone" />
        )}
      </button>
      <audio ref={ref} src={src} preload="none" />
    </div>
  );
}
